import { Dialog, Transition } from "@headlessui/react";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import { getGeoLocationData } from "../../utils/geoLocation";

const RegisterForm = () => {
  const { config } = useConfig();
  let { ServerBackEndUrl } = config;
  const [open, setOpen] = useState(false);
  const { setUser } = useContext(CadviewerContext);
  const cancelButtonRef = useRef(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    error: "",
  });
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const geolocationData = getGeoLocationData();

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const handleSignUp = useCallback(
    async (
      username: string,
      email: string,
      password: string,
      confirmPassword: string
    ) => {
      if (password !== confirmPassword) {
        setFormData((form) => ({ ...form, error: "Passwords do not match" }));
        return;
      }
      // validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setFormData((form) => ({ ...form, error: "Invalid email address" }));
        return;
      }

      // validate captcha — only when reCAPTCHA is enabled (a sitekey is configured)
      if (config.recaptchaSiteKey && !captchaToken) {
        setFormData((form) => ({
          ...form,
          error: "Please complete the reCAPTCHA verification",
        }));
        return;
      }

      setLoading(true);
      setFormData((form) => ({ ...form, error: "" }));
      try {
        const response = await fetch(
          ServerBackEndUrl +
            (ServerBackEndUrl.endsWith("/") ? "" : "/") +
            "auth/signup",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              username,
              email,
              password,
              geolocation: geolocationData || undefined,
              recaptchaToken: captchaToken,
            }),
          }
        );
        const data = await response.json();
        // check if status is not 200
        console.log({ data, response });
        if (!response.ok) {
          setFormData((form) => ({ ...form, error: data.error }));
          // Reset captcha if there's an error
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          setCaptchaToken(null);
        } else {
          if (data.data) {
            setOpen(false);
            setUser(data.data);
          } else {
            setRegistrationSuccess(true);
          }
          setFormData({
            username: "",
            email: "",
            password: "",
            confirmPassword: "",
            error: "",
          });
          setCaptchaToken(null);
        }
      } catch (error: any) {
        console.log({ error });
        setFormData((form) => ({
          ...form,
          error: "Something went wrong. Please try again later.",
        }));
        // Reset captcha on error
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
      } finally {
        setLoading(false);
      }
    },
    [ServerBackEndUrl, setUser, captchaToken]
  );

  useEffect(() => {
    if (!open) {
      setFormData({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        error: "",
      });
      setCaptchaToken(null);
    }
  }, [open]);

  return (
    <Fragment>
      <button
        id="register-button"
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className={`inline-flex items-center gap-x-1.5 rounded-md bg-primary-600 px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 mr-2
					${
            config.helperMethodsActionSize === "small"
              ? " text-xs my-1 mr-1 py-[2px] px-1"
              : config.helperMethodsActionSize === "large"
              ? " text-base my-2 mr-2 py-2 px-3"
              : " text-sm my-2 mr-2 py-1 px-3"
          }
					`}

        //className="inline-flex items-center gap-x-1.5 rounded-md bg-primary-600 px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 mr-2"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-6 h-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        Register
      </button>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  {!registrationSuccess ? (
                    <>
                      <div>
                        <div className="mt-3 sm:mt-5">
                          <div className={"flex items-center justify-between"}>
                            <Dialog.Title
                              as="h3"
                              className="text-xl font-semibold leading-6 text-gray-900"
                            >
                              Create an account
                            </Dialog.Title>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="currentColor"
                              className="size-6 cursor-pointer"
                              onClick={() => {
                                setOpen(false);
                              }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18 18 6M6 6l12 12"
                              />
                            </svg>
                          </div>
                          {formData.error !== "" && (
                            <div className="rounded-md bg-red-50 p-4 mt-4">
                              <div className="flex">
                                <div className="flex-shrink-0">
                                  <svg
                                    className="h-5 w-5 text-red-400"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <h3 className="text-sm font-medium text-red-800">
                                    {formData.error}
                                  </h3>
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="mt-4">
                            <div>
                              <label
                                htmlFor="username"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                Username
                              </label>
                              <div className="mt-2">
                                <input
                                  id="username"
                                  name="username"
                                  type="text"
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      username: e.target.value,
                                    })
                                  }
                                  value={formData.username}
                                  autoComplete="username"
                                  required
                                  className="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                                />
                              </div>
                            </div>
                            <div className="mt-2">
                              <label
                                htmlFor="email"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                Email
                              </label>
                              <div className="mt-2">
                                <input
                                  id="email"
                                  name="email"
                                  type="email"
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      email: e.target.value,
                                    })
                                  }
                                  value={formData.email}
                                  autoComplete="email"
                                  required
                                  className="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                                />
                              </div>
                            </div>

                            <div className="mt-2">
                              <label
                                htmlFor="password"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                Password
                              </label>
                              <div className="mt-2">
                                <input
                                  id="password"
                                  name="password"
                                  type="password"
                                  autoComplete="current-password"
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      password: e.target.value,
                                    })
                                  }
                                  value={formData.password}
                                  required
                                  className="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                                />
                              </div>
                            </div>
                            {/*	confirm password field */}
                            <div className="mt-2">
                              <label
                                htmlFor="confirm_password"
                                className="block text-sm font-medium leading-6 text-gray-900"
                              >
                                Confirm Password
                              </label>
                              <div className="mt-2">
                                <input
                                  id="confirm_password"
                                  name="confirm_password"
                                  type="password"
                                  autoComplete="current-password"
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      confirmPassword: e.target.value,
                                    })
                                  }
                                  value={formData.confirmPassword}
                                  required
                                  className="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5">
                        {config.recaptchaSiteKey ? (
                          <div className="flex justify-center mb-4">
                            <ReCAPTCHA
                              ref={recaptchaRef}
                              sitekey={config.recaptchaSiteKey}
                              onChange={handleCaptchaChange}
                            />
                          </div>
                        ) : null}
                        <button
                          type="button"
                          className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 sm:col-start-2"
                          onClick={() => {
                            handleSignUp(
                              formData.username,
                              formData.email,
                              formData.password,
                              formData.confirmPassword
                            );
                            console.log(
                              "FROM REGISTER BUTTON:gtag_report_conversion()"
                            );
                            (window as any).gtag_report_conversion();
                          }}
                          disabled={
                            formData.email === "" ||
                            formData.password === "" ||
                            formData.confirmPassword === "" ||
                            (!!config.recaptchaSiteKey && !captchaToken) ||
                            loading
                          }
                        >
                          {loading ? (
                            <div className="flex items-center justify-center">
                              <svg
                                className="animate-spin h-5 w-5 mr-2"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Registering...
                            </div>
                          ) : (
                            "Register"
                          )}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center">
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                        <svg
                          className="h-6 w-6 text-green-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.5 12.75l6 6 9-13.5"
                          />
                        </svg>
                      </div>
                      <div className="mt-3 text-center sm:mt-5">
                        <Dialog.Title
                          as="h3"
                          className="text-base font-semibold leading-6 text-gray-900"
                        >
                          Registration successful!
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Please check your email to activate your account.
                          </p>
                        </div>
                      </div>
                      <div className="mt-5 sm:mt-6">
                        <button
                          type="button"
                          className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
                          onClick={() => {
                            setOpen(false);
                            setTimeout(() => {
                              setRegistrationSuccess(false);
                            }, 1000);
                          }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </Fragment>
  );
};

export default RegisterForm;
